import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import parse from "html-react-parser";
import SEO from "@/components/seo/seo";
import { SITE } from "@/common/seo.config";
import RelatedSolutionsComponent from "@/common/related-solutions";
import SolutionStyleFinalCta from "@/common/solution-style-final-cta";
import { ArrowLine } from "@/assets";
import Image from "next/image";

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return true;
  if (typeof value === "boolean") return true;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return false;
};

const buildSolutionServiceSchema = (solution) => {
  const pageUrl = `${SITE.siteUrl}/solutions/${solution.slug}`;
  return {
    "@type": "Service",
    "@id": `${pageUrl}#service`,
    name: solution.title,
    description:
      solution.meta_description || solution.sub_title || solution.context || "",
    url: pageUrl,
    provider: { "@id": `${SITE.siteUrl}/#organization` },
    serviceType: solution.title,
    areaServed: "Worldwide",
    ...(solution.image ? { image: solution.image } : {}),
  };
};

const buildSolutionBreadcrumbSchema = (title, slug) => ({
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE.siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Solutions",
      item: `${SITE.siteUrl}/solutions`,
    },
    {
      "@type": "ListItem",
      position: 3,
      name: title,
      item: `${SITE.siteUrl}/solutions/${slug}`,
    },
  ],
});

const buildFaqSchema = (faqs = []) => {
  const entities = faqs
    .filter((item) => item?.question && item?.answer)
    .map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: String(item.answer)
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim(),
      },
    }));

  if (entities.length === 0) return null;

  return {
    "@type": "FAQPage",
    mainEntity: entities,
  };
};

function Card({ feature, index, activeIndex, setActiveIndex }) {
  const ref = useRef(null);
  const wasBelowRef = useRef(true);
  const step = 0.08;
  const minScale = 0.5;
  const depth = activeIndex >= index ? activeIndex - index + 1 : 0;
  const scale = depth > 0 ? Math.max(1 - depth * step, minScale) : 1;

  useEffect(() => {
    if (!ref.current?.nextElementSibling) return;
    const nextCard = ref.current.nextElementSibling;
    const handleScroll = () => {
      const nextRect = nextCard.getBoundingClientRect();
      const stickyTop =
        window.innerWidth >= 768
          ? window.innerHeight * 0.2
          : window.innerHeight * 0.15;
      const isBelow = nextRect.top > stickyTop;
      const wasBelow = wasBelowRef.current;
      if (wasBelow && !isBelow) setActiveIndex(index);
      if (!wasBelow && isBelow && activeIndex === index)
        setActiveIndex(index - 1);
      wasBelowRef.current = isBelow;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [index, activeIndex, setActiveIndex]);

  return (
    <div
      ref={ref}
      className="w-full solution-feature-card-sticky"
      style={{
        position: "sticky",
        top: "15%",
        transform: `scale(${scale})`,
        transformOrigin: "top center",
        zIndex: 1 + index,
        transition: "transform 0.35s ease",
        paddingTop: `${index * 7}px`,
      }}
    >
      <div
        className="flex w-full gap-3 rounded-xl border border-line bg-surface p-4 shadow-[0_12px_32px_-20px_rgba(15,28,26,0.2)] md:p-6"
      >
        <span className="text-3xl font-semibold text-main">0{index + 1}</span>
        <div className="min-w-0">
          <h3 className="text-xl md:text-2xl font-semibold text-ink mb-2">
            {feature.title}
          </h3>
          <p className="text-ink-soft mb-0">{feature.description}</p>
        </div>
      </div>
    </div>
  );
}

function SolutionsDetailsPage({ solutionData, relatedSolutionsData }) {
  const solutionDetails = solutionData || {};
  const relatedSolutions = relatedSolutionsData || [];
  const [activeIndex, setActiveIndex] = useState(-1);
  const [openId, setOpenId] = useState(null);

  const handleToggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  const show_context = hasValue(solutionDetails?.context);
  const show_who_is_for = hasValue(solutionDetails?.who_is_this_for);
  const show_who_is_not_for = hasValue(solutionDetails?.who_is_not_for);
  const show_real_problem_heading = hasValue(
    solutionDetails?.real_problem_heading,
  );
  const show_real_problem = hasValue(solutionDetails?.real_problem);
  const show_common_approach = hasValue(solutionDetails?.common_approach);
  const show_approach_falls_short = hasValue(
    solutionDetails?.approach_falls_short,
  );
  const show_core_features = hasValue(solutionDetails?.core_features);
  const show_how_we_approach = hasValue(solutionDetails?.how_we_approach);
  const show_outcomes = hasValue(solutionDetails?.outcomes);
  const show_micro_proof = hasValue(solutionDetails?.micro_proof);
  const show_faqs = hasValue(solutionDetails?.faqs);
  const show_main_content = hasValue(solutionDetails?.content);

  if (!solutionData) {
    return (
      <div className="container max-w-7xl py-14 text-center">
        <div className="text-ink-soft">
          Solution not found or failed to load.
        </div>
      </div>
    );
  }

  const solutionPageUrl = `${SITE.siteUrl}/solutions/${solutionDetails.slug}`;
  const structuredData = [
    {
      "@type": "WebPage",
      "@id": `${solutionPageUrl}#webpage`,
      url: solutionPageUrl,
      name: solutionDetails.title,
      description:
        solutionDetails?.meta_description ||
        solutionDetails?.sub_title ||
        solutionDetails?.context ||
        "",
      isPartOf: { "@id": `${SITE.siteUrl}/#website` },
      about: { "@id": `${SITE.siteUrl}/#organization` },
    },
    buildSolutionServiceSchema(solutionDetails),
    buildSolutionBreadcrumbSchema(solutionDetails.title, solutionDetails.slug),
  ];
  const faqSchema = buildFaqSchema(solutionDetails?.faqs);
  if (faqSchema) structuredData.push(faqSchema);

  return (
    <>
      <SEO
        pageTitle={solutionDetails?.title ?? ""}
        pageDescription={
          solutionDetails?.meta_description ||
          solutionDetails?.sub_title ||
          solutionDetails?.context ||
          ""
        }
        keywords={
          solutionDetails?.keywords
            ? String(solutionDetails.keywords)
            : "AI solutions, enterprise AI, AI agents, LLM applications, Nivalabs"
        }
        ogTitle={solutionDetails?.title ?? ""}
        ogUrl={solutionPageUrl}
        ogImage={solutionDetails?.image ?? SITE.defaultOgImage}
        ogType="website"
        structuredData={structuredData}
      />

      {/* Hero - with background image (solution image, or /images/sol_hero_bg.png, or gradient) */}
      <section className="theme-band-dark relative overflow-hidden py-12 md:py-16">
        <Image
          src="/solution_hero_bg.svg"
          alt={solutionDetails?.title || "Nivalabs Solution"}
          fill
          priority={true}
          fetchPriority="high"
          sizes="100vw"
          quality={70}
          className="object-cover z-[-1]"
        />

        <div className="container relative z-1 max-w-7xl">
          <div className="flex flex-wrap">
            <div className="lg:col-8">
              <h1
                className="syne-font mb-2 text-3xl font-bold text-white lg:text-5xl"
                style={{ lineHeight: "1.3" }}
              >
                {solutionDetails?.title}
              </h1>
              <p className="mb-4 font-normal text-slate-300">
                {solutionDetails?.sub_title ??
                  solutionDetails?.meta_description}
              </p>
              <Link
                href="/contact"
                className="solution-main-cta mt-6 inline-block rounded-full px-5 py-3 text-decoration-none fw-semibold text-main hover:text-white"
              >
                Talk to an AI Expert, Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Context */}
      {show_context && (
        <section
          className="py-12 md:py-16"
          // style={{ background: "rgba(14, 23, 33, 0.8)" }}
        >
          <div className="container max-w-7xl">
            <p className="text-ink-muted leading-relaxed text-lg">
              {solutionDetails?.context}
            </p>
          </div>
        </section>
      )}

      {/* Real Problem */}
      {show_real_problem_heading && show_real_problem && (
        <section className="bg-muted py-12 md:py-16">
          <div className="container max-w-7xl">
            <div className="flex items-start flex-wrap justify-center md:justify-between -m-3">
              <div
                className="col-12 md:col-5 lg:col-4 mb-4 mb-md-0 lg:sticky"
                style={{ top: "15%" }}
              >
                <h2 className="text-ink text-2xl md:text-4xl lg:text-5xl font-semibold capitalize">
                  The problem
                </h2>
                <div
                  className="w-10 bg-main rounded my-4"
                  style={{ height: "3px" }}
                />
              </div>
              <div className="col-12 md:col-7 lg:col-8">
                <h3 className="text-main font-semibold text-xl md:text-2xl mb-3">
                  {solutionDetails.real_problem_heading}
                </h3>
                <p className="text-ink-muted leading-relaxed text-lg whitespace-pre-line mb-0">
                  {solutionDetails?.real_problem}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How We Approach */}
      {show_how_we_approach && (
        <section className="py-12 md:py-16">
          <div className="container max-w-7xl">
            <h2 className="syne-font text-ink text-2xl md:text-4xl lg:text-5xl font-semibold capitalize mb-4 mb-md-5">
              How we approach it
            </h2>

            <div className="flex flex-wrap items-stretch justify-between -m-3 mt-6">
              {solutionDetails?.how_we_approach.map((item, index) => (
                <div key={index} className="col-12 md:col-6 lg:col-6">
                  <div
                    className="p-4 md:p-5 flex items-center rounded-xl gap-3 border border-line bg-surface"
                  >
                    <div className="text-4xl p-2 font-bold speedy-into-title">
                      0{index + 1}
                    </div>
                    <h3 className="text-ink text-lg md:text-2xl font-medium mb-0">
                      {item}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Core Features */}
      {show_core_features && (
        <section className="bg-muted py-12 md:py-16">
          <div className="container max-w-7xl">
            <div className="flex items-start flex-wrap justify-between -m-3">
              <div
                className="col-12 md:col-5 lg:col-4 mb-4 md:mb-0 md:sticky"
                style={{ top: "13%" }}
              >
                <h2
                  className="syne-font gradient-title text-3xl lg:text-5xl font-semibold"
                  style={{ lineHeight: 1.2 }}
                >
                  Core Features & Capabilities
                </h2>
                <div
                  className="w-10 rounded my-4"
                  style={{ height: "3px", background: "#1c8c80" }}
                />
              </div>
              <div className="col-12 md:col-7 lg:col-6">
                <div className="flex flex-col gap-5 items-center">
                  {solutionDetails?.core_features.map((feature, index) => (
                    <Card
                      key={feature?.id ?? index}
                      feature={feature}
                      index={index}
                      activeIndex={activeIndex}
                      setActiveIndex={setActiveIndex}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Micro Proof */}
      {show_micro_proof && (
        <section
          className="py-12 md:py-16"
          // style={{
          //   background:
          //     "radial-gradient(circle at top center, #2ae5cb30 0%, #0e1721 60%)",
          // }}
        >
          <div className="container max-w-7xl">
            <h2 className="syne-font  text-ink text-2xl md:text-3xl lg:text-5xl text-center font-bold mb-3">
              Why NivaLabs?
            </h2>
            <p className="mx-auto max-w-3xl text-center text-ink-muted">
              {solutionDetails?.micro_proof}
            </p>
          </div>
        </section>
      )}

      {/* Outcomes - PySquad-style: outer dashed circle, inner solid border, connecting line */}
      {show_outcomes && (
        <section className="py-12 md:py-16">
          <div className="container max-w-7xl">
            <h2 className="syne-font text-center text-ink text-3xl lg:text-5xl font-semibold mb-5">
              Architecure Overview
            </h2>

            <div className="flex flex-wrap text-center justify-center items-stretch solution-outcomes-line -m-3">
              {solutionDetails?.outcomes?.map((item, index) => (
                <div
                  key={item?.id ?? index}
                  className="flex flex-col col-12 md:col-6 lg:col-3 items-center"
                >
                  <div className="solution-outcomes-circle">
                    <div className="solution-outcomes-circle-inner">
                      0{index + 1}
                    </div>
                  </div>
                  <h4 className="mt-6 mb-0 font-semibold text-lg text-ink capitalize">
                    {typeof item === "string" ? item : (item?.title ?? item)}
                  </h4>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main HTML Content */}
      {show_main_content && (
        <section className="py-12 md:py-16">
          <div className="container max-w-7xl">
            <div className="flex justify-center">
              <div className="w-full max-w-4xl">
                <div className="blog-content text-ink-muted">
                  {typeof solutionDetails.content === "string"
                    ? parse(solutionDetails.content)
                    : null}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <SolutionStyleFinalCta
        heading={
          solutionDetails?.final_cta_text || "Looking for similar solutions?"
        }
        buttonHref={solutionDetails?.final_cta_url || "/contact"}
      />

      {/* FAQs */}
      {show_faqs && (
        <section className="py-12 md:py-16">
          <div className="container max-w-7xl">
            <h2 className="syne-font text-ink text-3xl md:text-4xl font-semibold mb-5">
              Frequently asked questions
            </h2>

            <div className="border-t !border-[#90cac43f]">
              {solutionDetails?.faqs.map((item, index) => {
                const isOpen = openId === index;
                return (
                  <div
                    key={index}
                    className="border-b !border-[#90cac43f] py-4"
                  >
                    <button
                      type="button"
                      onClick={() => handleToggle(index)}
                      className="flex w-full items-center justify-between text-start  text-ink bg-transparent border-0 p-0"
                    >
                      <span className="font-medium text-base md:text-lg">
                        {item.question}
                      </span>
                      <span
                        className="flex items-center justify-center rounded-full text-main"
                        style={{
                          width: "32px",
                          height: "32px",
                          border: "1px solid #5AADA4",
                        }}
                      >
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>
                    <div
                      className="overflow-hidden transition-all duration-300"
                      style={{
                        maxHeight: isOpen ? "300px" : 0,
                        opacity: isOpen ? 1 : 0,
                      }}
                    >
                      <p className="text-ink-soft pt-3 mb-0 pe-5">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* About NivaLabs */}
      <section className="landing-section landing-section-muted">
        <div className="container max-w-7xl">
          <h2 className="syne-font mb-5 text-center text-3xl font-semibold text-ink md:text-4xl">
            About Nivalabs AI
          </h2>
          <p className="mx-auto max-w-4xl text-center text-lg leading-relaxed text-ink-muted">
            Nivalabs is an AI development company specializing in AI agent
            development, custom LLM applications, and workflow automation
            systems. We help businesses apply artificial intelligence to real
            operational challenges through scalable, production-ready solutions
            designed for logistics, aviation, marina operations, manufacturers
            and enterprise workflows.
          </p>
        </div>
      </section>

      <RelatedSolutionsComponent data={relatedSolutionsData} />
    </>
  );
}

export default SolutionsDetailsPage;
